import { request, requestPage } from '@/lib/apiClient';
import { endpoints } from '@/features/endpoints';
import type { AccountStatus, AdminUserDetail, AdminUserSummary, Role } from '@/types/domain';
import type { Page } from '@/types/api';

export type UserListParams = {
  role?: Role;
  status?: string;
  /**
   * Coach approval state. Ignored by the API for any role but `trainer`.
   *
   * The Coaches screen sends `approved` by default: a coach who has not cleared
   * moderation is not on the roster, they are in the Coach Applications queue.
   */
  approval?: string;
  q?: string;
  limit?: number;
  cursor?: string;
};

/**
 * `GET /admin/users`.
 *
 * Search is SERVER-side. Filtering an already-fetched page in the browser looks
 * identical on a seeded dev database and is completely wrong in production,
 * where the page holds 20 of 40,000 members.
 */
export function fetchUsers(
  params: UserListParams,
  signal?: AbortSignal,
): Promise<Page<AdminUserSummary>> {
  return requestPage<AdminUserSummary>(endpoints.users.list, {
    query: {
      role: params.role,
      status: params.status,
      approval: params.approval,
      q: params.q,
      limit: params.limit ?? 20,
      cursor: params.cursor,
    },
    ...(signal ? { signal } : {}),
  });
}

export function fetchUser(id: string, signal?: AbortSignal): Promise<AdminUserDetail> {
  return request<AdminUserDetail>(endpoints.users.detail(id), signal ? { signal } : {});
}

/** The three status transitions staff can make, each requiring a reason. */
export type StatusChange = {
  status: Extract<AccountStatus, 'active' | 'suspended' | 'blocked'>;
  reason: string;
};

export function changeUserStatus(id: string, change: StatusChange): Promise<AdminUserDetail> {
  return request<AdminUserDetail>(endpoints.users.status(id), {
    method: 'PATCH',
    body: change,
  });
}

/**
 * `PATCH /admin/trainers/:id/featured`.
 *
 * Returns the full account detail rather than the trainer card alone, so the
 * drawer repaints from one response instead of firing a second request to find
 * out what changed underneath it.
 */
export function setCoachFeatured(id: string, featured: boolean): Promise<AdminUserDetail> {
  return request<AdminUserDetail>(endpoints.trainers.featured(id), {
    method: 'PATCH',
    body: { featured },
  });
}

/**
 * `PATCH /admin/trainers/:id/rating`.
 *
 * Corrects or clears the STARTING rating set at approval. `null` removes it and
 * returns the coach to "New".
 *
 * The API refuses this once the coach has real reviews: at that point the
 * rating is an average of what members said, and overwriting it would not be
 * seeding a cold start, it would be editing their verdicts.
 */
export function setCoachSeedRating(
  id: string,
  initialRating: number | null,
  initialRatingNote?: string,
): Promise<AdminUserDetail> {
  return request<AdminUserDetail>(endpoints.trainers.rating(id), {
    method: 'PATCH',
    body: { initialRating, ...(initialRatingNote ? { initialRatingNote } : {}) },
  });
}

/**
 * `DELETE /admin/users/:id`.
 *
 * SOFT deletion - the account is marked deleted, signed out and disabled at the
 * identity provider, but the row stays so payments, subscriptions and the audit
 * log keep resolving. See the note on `deleteUser` in the backend service.
 *
 * `confirm` is sent because the SERVER requires it, not to satisfy the dialog.
 * A confirmation that only exists in the UI is one every future caller skips.
 */
export function deleteUser(id: string, reason: string): Promise<AdminUserDetail> {
  return request<AdminUserDetail>(endpoints.users.remove(id), {
    method: 'DELETE',
    body: { confirm: 'DELETE', reason },
  });
}
